import React, { useState } from "react";
import { createPortal } from "react-dom";
import {
  Bot, Loader2, RefreshCw, X,
  BarChart3, Wallet, HeartHandshake, Cake, Users, FileText,
  ChevronRight, Copy, Download
} from "lucide-react";
import { AI_ANALYSIS_TYPES } from "./useStatisticsAiBriefing";

const TYPE_ICONS = {
  briefing: FileText,
  attendance: BarChart3,
  finance: Wallet,
  pastoral: HeartHandshake,
  birthday: Cake,
  teacher: Users,
};

const TYPE_COLORS = {
  briefing: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100', activeBg: 'bg-violet-600' },
  attendance: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', activeBg: 'bg-blue-600' },
  finance: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', activeBg: 'bg-emerald-600' },
  pastoral: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-100', activeBg: 'bg-rose-600' },
  birthday: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100', activeBg: 'bg-amber-600' },
  teacher: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-100', activeBg: 'bg-sky-600' },
};

export default function StatisticsAiBriefingModal({
  aiResult,
  isAiLoading,
  isOpen,
  onClose,
  onRefresh,
  overlayClassName = "",
  showRefresh = false,
  usePortal = false,
  activeAnalysisType = 'briefing',
  onChangeAnalysisType,
}) {
  const [copyStatus, setCopyStatus] = useState("idle"); // 'idle' | 'copied'

  if (!isOpen) return null;

  const activeColors = TYPE_COLORS[activeAnalysisType] || TYPE_COLORS.briefing;

  const modal = (
    <div className={`fixed inset-0 z-[99999] flex items-center justify-center p-3 md:p-4 bg-black/30 backdrop-blur-sm ${overlayClassName}`} onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-100" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className={`${activeColors.bg} p-2.5 rounded-2xl shadow-sm`}>
              <Bot size={22} className={activeColors.text} />
            </div>
            <div>
              <h3 className="font-black text-lg text-slate-800 font-do-hyeon">Aaron AI 분석</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Smart Ministry Analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {showRefresh && (
              <button
                onClick={onRefresh}
                disabled={isAiLoading}
                className="p-2.5 hover:bg-slate-100 rounded-xl transition text-slate-400 hover:text-slate-600 disabled:opacity-50"
                title="다시 분석하기"
              >
                <RefreshCw size={16} className={isAiLoading ? "animate-spin" : ""} />
              </button>
            )}
            <button onClick={onClose} className="p-2.5 hover:bg-slate-100 rounded-xl transition text-slate-400 hover:text-slate-600">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Analysis Type Tabs */}
        <div className="px-4 pt-4 pb-2 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max">
            {AI_ANALYSIS_TYPES.map((type) => {
              const Icon = TYPE_ICONS[type.id] || FileText;
              const colors = TYPE_COLORS[type.id] || TYPE_COLORS.briefing;
              const isActive = activeAnalysisType === type.id;
              return (
                <button
                  key={type.id}
                  onClick={() => {
                    if (onChangeAnalysisType && !isAiLoading) {
                      onChangeAnalysisType(type.id);
                    }
                  }}
                  disabled={isAiLoading}
                  className={`
                    flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold whitespace-nowrap
                    transition-all duration-200
                    ${isActive
                      ? `${colors.activeBg} text-white shadow-md`
                      : `${colors.bg} ${colors.text} border ${colors.border} hover:shadow-sm`
                    }
                    ${isAiLoading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <Icon size={13} strokeWidth={2.5} />
                  {type.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Result Body */}
        <div className="p-5 max-h-[55vh] overflow-y-auto custom-scrollbar">
          {isAiLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <div className="relative">
                <div className={`w-16 h-16 rounded-full ${activeColors.bg} animate-ping absolute inset-0 opacity-30`} />
                <div className={`w-16 h-16 rounded-full ${activeColors.bg} flex items-center justify-center relative`}>
                  <Loader2 size={28} className={`animate-spin ${activeColors.text}`} />
                </div>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-slate-600">데이터를 분석하고 있습니다</p>
                <p className="text-[11px] text-slate-400 mt-1">잠시만 기다려주세요...</p>
              </div>
            </div>
          ) : aiResult ? (
            <div className="space-y-3">
              {aiResult.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <div key={i} className="h-2" />;

                // Bold headers
                if (/^\*\*(.+)\*\*$/.test(trimmed)) {
                  return (
                    <div key={i} className="flex items-center gap-2 pt-3 first:pt-0">
                      <div className={`w-1 h-5 rounded-full ${activeColors.activeBg}`} />
                      <h4 className="font-black text-slate-800 text-[15px]">{trimmed.replace(/\*\*/g, '')}</h4>
                    </div>
                  );
                }

                // Bullet points
                if (/^[•\-\*]\s/.test(trimmed)) {
                  return (
                    <div key={i} className="flex items-start gap-2.5 pl-2">
                      <ChevronRight size={12} className={`${activeColors.text} mt-1 flex-shrink-0`} strokeWidth={3} />
                      <span className="text-sm text-slate-600 font-medium leading-relaxed">{trimmed.replace(/^[•\-\*]\s+/, '').replace(/\*\*/g, '')}</span>
                    </div>
                  );
                }

                // Numbered lines
                if (/^\d+[\.\)]\s/.test(trimmed)) {
                  return (
                    <div key={i} className={`flex items-start gap-2.5 p-3 rounded-2xl ${activeColors.bg} border ${activeColors.border}`}>
                      <span className={`text-xs font-black ${activeColors.text} mt-0.5`}>{trimmed.match(/^\d+/)?.[0]}</span>
                      <span className="text-sm text-slate-700 font-medium leading-relaxed">{trimmed.replace(/^\d+[\.\)]\s*/, '').replace(/\*\*/g, '')}</span>
                    </div>
                  );
                }

                // Regular text
                return <p key={i} className="text-sm text-slate-600 font-medium leading-relaxed">{trimmed.replace(/\*\*/g, '')}</p>;
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 gap-3 opacity-40">
              <Bot size={40} />
              <p className="text-xs font-bold">분석 결과가 여기에 표시됩니다</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <div className="flex gap-2">
            {aiResult && !isAiLoading && (
              <>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(aiResult);
                      setCopyStatus("copied");
                      setTimeout(() => setCopyStatus("idle"), 2000);
                    } catch (err) {
                      console.error("Copy failed:", err);
                    }
                  }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold transition-all active:scale-95 ${copyStatus === "copied" ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
                >
                  <Copy size={14} />
                  {copyStatus === "copied" ? "복사완료!" : "복사하기"}
                </button>
                <button
                  onClick={() => {
                    const blob = new Blob([aiResult], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `Aaron_AI_Briefing_${activeAnalysisType}_${new Date().toISOString().slice(0, 10)}.txt`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-white text-slate-600 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50 transition active:scale-95"
                >
                  <Download size={14} />
                  저장
                </button>
              </>
            )}
          </div>
          <button onClick={onClose} className="px-5 py-2.5 bg-slate-800 text-white font-bold rounded-xl hover:bg-slate-900 transition text-sm active:scale-95">
            확인
          </button>
        </div>
      </div>
    </div>
  );

  return usePortal ? createPortal(modal, document.body) : modal;
}
